<template>
  <v-container tag="section">
    <v-progress-linear v-if="loadingCatalogo" indeterminate color="primary" class="mb-4" />

    <v-alert
      v-if="catalogoErro"
      type="error"
      outlined
      class="mx-auto"
      max-width="720"
    >
      <div>Não foi possível carregar as informações de criação.</div>
    </v-alert>

    <template v-else-if="renderizacaoLiberada">
      <div class="conciliacao-wizard-shell mx-auto">
        <material-wizard
          v-model="tab"
          :title="title"
          subtitle="Preencha as informações para gerar sua conciliação."
          :tabs="tabs"
          :available-steps="availableSteps"
          :loading="wizardLoading"
          :maxWidth="'1200'"
          btnSaveCustomId="btn-save-conciliacao-v3"
          @click:next="next()"
          @click:prev="back()"
        >
          <conciliacao-wizard-tabs
            ref="wizardTabs"
            :entity="entity"
            :esquemas="catalogo.esquemas"
            :templates="catalogo.templates"
            :tipo-padrao="catalogo.tipoPadrao"
            :uploading-partida="uploadingPartida"
            :uploading-referencia="uploadingReferencia"
            :loading-sugestao="loadingSugestao"
            :loading-rodadas="loadingRodadas || rodadasBloqueadas"
            :rodadas="rodadasPagina"
            :rodadas-has-more="rodadasTemProxima"
            :rodadas-has-prev="rodadasPageIndex > 0"
            :rodadas-page-index="rodadasPageIndex"
            :rodadas-total="rodadasTotalExibido"
            :rodadas-campos-config="rodadasCamposConfig"
            :rodadas-estado-por-campo="rodadasEstadoPorCampo"
            :rodadas-tipo-ordenacao="rodadasTipoOrdenacao"
            @inicioTabAvailable="validTabs[0] = $event"
            @partidaTabAvailable="validTabs[1] = $event"
            @referenciaTabAvailable="validTabs[2] = $event"
            @rodadasTabAvailable="validTabs[3] = $event"
            @btnFinishAvailable="validTabs[4] = $event"
            @inicio-montado="ensureTabBaselines"
            @upload-partida="uploadPartida"
            @upload-referencia="uploadReferencia"
            @aplicar-sugestao="aplicarSugestao"
            @rodadas-pagina-anterior="mudarPaginaRodadas('prev')"
            @rodadas-pagina-proxima="mudarPaginaRodadas('next')"
            @rodadas-prioridade-change="onPrioridadeChange"
            @rodadas-mover="onRodadaMovida"
            @rodadas-editar-campos="onRodadaEditada"
            @rodadas-adicionar="onRodadaAdicionada"
            @rodadas-duplicar="onRodadaDuplicada"
            @rodadas-remover="onRodadaRemovida"
          />
        </material-wizard>

        <v-progress-linear
          v-if="wizardLoading"
          indeterminate
          color="primary"
          absolute
          top
          height="4"
          class="conciliacao-wizard-shell__progress"
        />
      </div>
    </template>
  </v-container>
</template>

<script>
  import ConciliacaoService from '@/services/ConciliacaoService';
  import rodadasLoteMixins from '@/mixins/conciliacoesV3/rodadasLoteMixins';

  export default {
    name: 'ConciliacaoEdit',
    components: {
      MaterialWizard: () => import('@/components/base/MaterialWizard.vue'),
      ConciliacaoWizardTabs: () => import('@/components/conciliacoesV3/ConciliacaoWizardTabs.vue')
    },
    mixins: [rodadasLoteMixins],
    data() {
      return {
        tab: 0,
        tabs: ['Início', 'Partida', 'Referência', 'Rodadas'],
        availableSteps: [0],
        validTabs: { 0: true, 1: false, 2: false, 3: false, 4: false },
        loading: false,
        uploadingPartida: false,
        uploadingReferencia: false,
        loadingSugestao: false,
        entityId: null,
        entity: null,
        savedSnapshots: {},
        tabNavigationLock: false,
        profilingTimers: { PARTIDA: null, REFERENCIA: null },
        loadingCatalogo: false,
        catalogoErro: false,
        catalogo: {
          esquemas: {},
          templates: [],
          tipoPadrao: 'STANDARD'
        }
      };
    },
    computed: {
      title() {
        return this.entityId ? 'Editando Conciliação' : 'Adicionar Nova Conciliação';
      },
      renderizacaoLiberada() {
        if (this.catalogoErro || this.loadingCatalogo) return false;
        if (!Object.keys(this.catalogo.esquemas || {}).length) return false;
        if (!this.$route.params.id) return true;
        return !!(this.$route.params.id && this.entity);
      },
      stepsProps() {
        return Object.values(this.validTabs).join('|');
      },
      wizardLoading() {
        return this.loading || this.uploadingPartida || this.uploadingReferencia;
      }
    },
    created() {
      this.carregarCatalogo();
      if (this.$route.params.id) {
        this.entityId = this.$route.params.id;
        this.loadEntity(this.entityId);
      }
    },
    beforeDestroy() {
      Object.keys(this.profilingTimers).forEach((papel) => this.stopProfilingPoll(papel));
    },
    methods: {
      apiError(err, fallback) {
        const body = err?.data || err?.response?.data;
        const details = body?.error?.details;
        if (details?.erros?.length) {
          return details.erros.map((e) => e.mensagem || e.message).join(' | ');
        }
        return body?.message || body?.error?.message || fallback;
      },
      catalogoValido(payload) {
        const esquemas = payload?.esquemas || {};
        const templates = payload?.templates || [];
        const tiposOk = ['FLEXIBILIZACAO', 'CORINGA', 'MASCARA', 'TRATAMENTO_POR_TIPO'].every(
          (tipo) => Array.isArray(esquemas[tipo]?.parametros) && esquemas[tipo].parametros.length
        );
        const temStandard = templates.some((template) => {
          const nome = String(template?.nome || '').toUpperCase();
          return nome === 'STANDARD' || nome.includes('STANDARD');
        });
        return tiposOk && temStandard;
      },
      carregarCatalogo() {
        this.loadingCatalogo = true;
        this.catalogoErro = false;
        ConciliacaoService.catalogoCriacao()
          .then(({ data }) => {
            const payload = data.data || {};
            if (!this.catalogoValido(payload)) {
              this.catalogoErro = true;
              return;
            }
            this.catalogo = {
              esquemas: payload.esquemas,
              templates: payload.templates || [],
              tipoPadrao: payload.tipoPadrao || 'STANDARD'
            };
          })
          .catch(() => {
            this.catalogoErro = true;
          })
          .finally(() => {
            this.loadingCatalogo = false;
          });
      },
      applyEntity(entity) {
        if (this.entityId && this.entityId !== entity.id) {
          this.savedSnapshots = {};
        }
        this.entity = entity;
        this.entityId = entity.id;
        const rodadasConfig = entity?.parametros?.rodadasConfig;
        if (rodadasConfig?.campos?.length) {
          this.rodadasCamposConfig = rodadasConfig.campos.map(String);
          this.rodadasEstadoPorCampo = rodadasConfig.estadoPorCampo || {};
        } else {
          this.rodadasCamposConfig = [];
          this.rodadasEstadoPorCampo = {};
        }
        this.rodadasTipoOrdenacao = rodadasConfig?.tipoOrdenacao || null;
        this.liberarAbasPreenchidas(entity);
        this.$nextTick(() => {
          this.$refs.wizardTabs?.refreshAvailability();
          this.ensureTabBaselines();
        });
      },

      liberarAbasPreenchidas(entity) {
        if (!entity || !entity.id) return;

        this.validTabs = {
          0: true,
          1: !!(entity.inicio_ok || entity.partida_ok || entity.referencia_ok || entity.rodadas_ok),
          2: !!(entity.partida_ok || entity.referencia_ok || entity.rodadas_ok),
          3: !!(entity.referencia_ok || entity.rodadas_ok),
          4: entity.rodadas_ok ? undefined : false
        };
        this.rebuildAvailableSteps();
      },

      rebuildAvailableSteps() {
        const steps = [0];
        for (let i = 1; i <= this.tabs.length; i++) {
          if (this.validTabs[i] && this.validTabs[i - 1]) {
            steps.push(i);
          }
        }
        if (this.validTabs[this.tabs.length - 1] && this.validTabs[this.tabs.length] === undefined) {
          steps.push(this.tabs.length);
        }
        this.availableSteps = steps;
      },

      tabSnapshot(tabIndex) {
        const tabs = this.$refs.wizardTabs;
        if (!tabs) return '';
        if (tabIndex === 0) return JSON.stringify(tabs.getInicioPayload());
        if (tabIndex === 1) return JSON.stringify(tabs.getPartidaPayload());
        if (tabIndex === 2) return JSON.stringify(tabs.getReferenciaPayload());
        if (tabIndex === 3) {
          return JSON.stringify({ payload: tabs.getRodadasPayload() });
        }
        return '';
      },
      markTabSaved(tabIndex) {
        this.$set(this.savedSnapshots, tabIndex, this.tabSnapshot(tabIndex));
      },
      tabHasPersistedContent(tabIndex) {
        if (tabIndex === 0) return true;
        const snapshot = this.tabSnapshot(tabIndex);
        if (!snapshot) return false;
        try {
          const parsed = JSON.parse(snapshot);
          if (tabIndex === 3) return Array.isArray(parsed?.payload?.rodadas) && parsed.payload.rodadas.length > 0;
          return Array.isArray(parsed?.campos) && parsed.campos.length > 0;
        } catch {
          return false;
        }
      },
      captureBaselineIfMissing(tabIndex) {
        const flags = ['inicio_ok', 'partida_ok', 'referencia_ok', 'rodadas_ok'];
        if (!this.entity?.[flags[tabIndex]] || this.savedSnapshots[tabIndex] != null) return;
        if (tabIndex > 0 && !this.tabHasPersistedContent(tabIndex)) return;
        const snapshot = this.tabSnapshot(tabIndex);
        if (!snapshot) return;
        this.$set(this.savedSnapshots, tabIndex, snapshot);
      },
      ensureTabBaselines() {
        this.$nextTick(() => {
          [0, 1, 2, 3].forEach((index) => this.captureBaselineIfMissing(index));
        });
      },
      tabNeedsSave(tabIndex) {
        const flags = ['inicio_ok', 'partida_ok', 'referencia_ok', 'rodadas_ok'];
        if (!this.entity?.[flags[tabIndex]]) return true;
        this.captureBaselineIfMissing(tabIndex);
        if (this.savedSnapshots[tabIndex] == null) return false;
        return this.tabSnapshot(tabIndex) !== this.savedSnapshots[tabIndex];
      },
      validateCurrentTab() {
        const tabs = this.$refs.wizardTabs;
        if (!tabs) return 'Formulário indisponível.';
        return [tabs.validateInicio, tabs.validatePartida, tabs.validateReferencia, tabs.validateRodadas][
          this.tab
        ]?.call(tabs);
      },
      goToTab(index) {
        this.tabNavigationLock = true;
        this.tab = index;
        this.$nextTick(() => {
          this.captureBaselineIfMissing(index);
          this.tabNavigationLock = false;
          if (index === 3 && this.entityId) {
            this.carregarRodadas('reset');
          }
        });
      },
      loadEntity(id) {
        this.loading = true;
        ConciliacaoService.getEntity(id)
          .then(({ data }) => {
            this.applyEntity(data.data);
            this.$nextTick(() => this.maybePollProfiling(data.data));
          })
          .catch((err) => this.$toast.error(this.apiError(err, 'Erro ao carregar.'), '', { position: 'topRight' }))
          .finally(() => {
            this.loading = false;
          });
      },
      stopProfilingPoll(papel) {
        if (this.profilingTimers[papel]) {
          clearTimeout(this.profilingTimers[papel]);
          this.profilingTimers[papel] = null;
        }
      },
      maybePollProfiling(entity) {
        const bases = entity?.parametros?.bases || {};
        ['PARTIDA', 'REFERENCIA'].forEach((papel) => {
          const base = bases[papel];
          if (!base?.campos?.length) return;
          const status = String(base.profilingStatus || '').toUpperCase();
          const hasProfiling = (base.campos || []).some((c) => c && c.profiling);
          if (status !== 'CONCLUIDO' || !hasProfiling) {
            this.pollProfiling(papel);
          }
        });
      },
      pollProfiling(papel, attempt = 0) {
        if (!this.entityId) return;
        this.stopProfilingPoll(papel);
        const method = papel === 'REFERENCIA' ? 'profilingReferencia' : 'profilingPartida';
        ConciliacaoService[method](this.entityId)
          .then(({ data }) => {
            const payload = data.data || {};
            this.$refs.wizardTabs?.applyProfiling(papel, payload);
            const status = String(payload.profilingStatus || '').toUpperCase();
            if (status === 'PENDENTE') {
              this.profilingTimers[papel] = setTimeout(
                () => this.pollProfiling(papel, attempt + 1),
                1000 * Math.pow(2, attempt)
              );
            }
          })
          .catch(() => {
            this.profilingTimers[papel] = setTimeout(
              () => this.pollProfiling(papel, attempt + 1),
              1000 * Math.pow(2, attempt)
            );
          });
      },
      next() {
        if (this.tab === 3) return this.leaveTab(3, { executar: true });
        return this.leaveTab(this.tab + 1);
      },
      back() {
        if (this.tab <= 0) return;
        if (this.tab === 3) return this.leaveTab(this.tab - 1);
        this.tabNavigationLock = true;
        this.tab--;
        this.$nextTick(() => {
          this.captureBaselineIfMissing(this.tab);
          this.tabNavigationLock = false;
        });
      },
      async leaveTab(target, { executar = false } = {}) {
        if (this.loading) return false;

        if (this.tab === 3) {
          return this.sairDaAbaRodadas(target, { executar });
        }

        if (target < this.tab) {
          this.goToTab(target);
          return true;
        }

        const valid = this.validateCurrentTab();
        if (valid !== true) {
          this.$toast.error(valid, '', { position: 'topRight' });
          return false;
        }

        if (!this.tabNeedsSave(this.tab)) {
          if (executar) {
            this.loading = true;
            try {
              await this.executarConciliacao();
              return true;
            } catch {
              return false;
            } finally {
              this.loading = false;
            }
          }
          this.goToTab(target);
          return true;
        }

        this.loading = true;
        try {
          await this.saveCurrentTab(this.tab);
          this.markTabSaved(this.tab);
          if (executar) {
            await this.executarConciliacao();
          } else {
            this.goToTab(target);
          }
          return true;
        } catch {
          return false;
        } finally {
          this.loading = false;
        }
      },
      async sairDaAbaRodadas(target, { executar = false } = {}) {
        if (this.loadingRodadas) return false;
        if (executar) {
          const valid = this.validateCurrentTab();
          if (valid !== true) {
            this.$toast.error(valid, '', { position: 'topRight' });
            return false;
          }
        }

        this.loading = true;
        try {
          await this.salvarLoteRodadas({ marcarConcluida: executar });
          if (executar) {
            await this.executarConciliacao();
          } else {
            this.goToTab(target);
          }
          return true;
        } catch {
          return false;
        } finally {
          this.loading = false;
        }
      },
      saveCurrentTab(tabIndex) {
        const tabs = this.$refs.wizardTabs;
        const erros = {
          0: 'Erro ao salvar início.',
          1: 'Erro ao mapear partida.',
          2: 'Erro ao mapear referência.'
        };

        let request;
        if (tabIndex === 0) {
          request = ConciliacaoService.salvarInicio(tabs.getInicioPayload(), this.entityId).then(({ data }) => {
            this.applyEntity(data.data);
            if (!this.$route.params.id) {
              this.$router.replace(`/conciliacoes/edit/${data.data.id}`);
            }
            this.$toast.success(data.message || 'Início salvo.', '', { position: 'topRight' });
          });
        } else if (tabIndex === 1) {
          request = ConciliacaoService.mapearPartida(this.entityId, tabs.getPartidaPayload()).then(({ data }) => {
            this.applyEntity(data.data.entity);
            this.$toast.success(data.message || 'Partida salva.', '', { position: 'topRight' });
          });
        } else if (tabIndex === 2) {
          request = ConciliacaoService.mapearReferencia(this.entityId, tabs.getReferenciaPayload()).then(({ data }) => {
            this.applyEntity(data.data.entity);
            this.$toast.success(data.message || 'Referência salva.', '', { position: 'topRight' });
          });
        } else {
          return Promise.reject(new Error('Aba inválida.'));
        }

        return request.catch((err) => {
          this.$toast.error(this.apiError(err, erros[tabIndex]), '', { position: 'topRight' });
          return Promise.reject(err);
        });
      },
      executarConciliacao() {
        return ConciliacaoService.executar(this.entityId)
          .then((resp) => {
            this.$toast.success(resp.data?.message || 'Execução solicitada.', '', { position: 'topRight' });
            this.$router.push({ name: 'Conciliação Histórico' });
          })
          .catch((err) => {
            this.$toast.error(this.apiError(err, 'Erro ao executar conciliação.'), '', { position: 'topRight' });
            return Promise.reject(err);
          });
      },
      uploadPartida(formData) {
        if (!this.entityId) {
          this.$toast.error('Salve o Início (Próximo) antes de enviar o arquivo.', '', { position: 'topRight' });
          return;
        }
        this.uploadingPartida = true;
        ConciliacaoService.uploadPartida(this.entityId, formData)
          .then(({ data }) => {
            this.applyEntity(data.data.entity);
            this.$refs.wizardTabs.applyUploadPartida(data.data.upload);
            this.pollProfiling('PARTIDA');
            this.$toast.success(data.message || 'Upload concluído.', '', { position: 'topRight' });
          })
          .catch((err) => this.$toast.error(this.apiError(err, 'Erro no upload.'), '', { position: 'topRight' }))
          .finally(() => {
            this.uploadingPartida = false;
          });
      },
      uploadReferencia(formData) {
        if (!this.entityId) {
          this.$toast.error('Salve as etapas anteriores antes de enviar o arquivo.', '', { position: 'topRight' });
          return;
        }
        this.uploadingReferencia = true;
        ConciliacaoService.uploadReferencia(this.entityId, formData)
          .then(({ data }) => {
            this.applyEntity(data.data.entity);
            this.$refs.wizardTabs.applyUploadReferencia(data.data.upload);
            this.pollProfiling('REFERENCIA');
            this.$toast.success(data.message || 'Upload concluído.', '', { position: 'topRight' });
          })
          .catch((err) => this.$toast.error(this.apiError(err, 'Erro no upload.'), '', { position: 'topRight' }))
          .finally(() => {
            this.uploadingReferencia = false;
          });
      },
      aplicarSugestao() {
        this.loadingSugestao = true;
        ConciliacaoService.sugestaoMapeamento(this.entityId)
          .then(({ data }) => {
            const aplicadas = this.$refs.wizardTabs.aplicarSugestoesReferencia(data.data?.sugestoes || []);
            if (aplicadas) {
              this.$toast.success('Sugestão aplicada aos correspondentes. Salve para gravar.', '', {
                position: 'topRight'
              });
            } else {
              this.$toast.info('Nenhuma correspondência sugerida.', '', { position: 'topRight' });
            }
          })
          .catch((err) => this.$toast.error(this.apiError(err, 'Erro ao aplicar sugestão.'), '', { position: 'topRight' }))
          .finally(() => {
            this.loadingSugestao = false;
          });
      },
    },
    watch: {
      tab(newTab, oldTab) {
        if (this.tabNavigationLock || this.loading) return;
        if (newTab == null || oldTab == null || newTab === oldTab) return;

        if (newTab < oldTab) {
          if (oldTab === 3) {
            this.tabNavigationLock = true;
            this.tab = oldTab;
            this.$nextTick(() => {
              this.leaveTab(newTab).finally(() => {
                this.tabNavigationLock = false;
              });
            });
            return;
          }
          if (newTab === 3 && this.entityId) {
            this.carregarRodadas('reset');
          }
          return;
        }

        this.tabNavigationLock = true;
        this.tab = oldTab;
        this.$nextTick(() => {
          this.leaveTab(newTab).finally(() => {
            this.tabNavigationLock = false;
          });
        });
      },
      stepsProps() {
        this.rebuildAvailableSteps();
      }
    }
  };
</script>

<style scoped>
  .conciliacao-wizard-shell {
    position: relative;
    max-width: 1200px;
  }

  .conciliacao-wizard-shell__progress {
    left: 0;
    right: 0;
    z-index: 2;
    border-radius: 4px 4px 0 0;
  }

  .conciliacao-wizard-shell >>> .v-card--wizard {
    overflow: hidden;
  }
</style>
